<template>
  <div>
    <div class="page-header">
      <h1 class="page-title">Usuários do Check-in</h1>
      <button class="btn-primary" @click="openNew">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
        <span>Novo usuário</span>
      </button>
    </div>

    <p class="page-subtitle">
      <template v-if="authStore.isRoot">
        Todos os usuários do sistema. Você pode criar admins e voluntários.
      </template>
      <template v-else>
        Você pode criar e remover voluntários (CHECKIN). Demais usuários ficam visíveis em modo somente leitura.
      </template>
    </p>

    <SkeletonLoader v-if="store.loading && store.admins.length === 0" variant="table-row" :count="4" />

    <div class="admins-list" v-else-if="store.admins.length > 0">
      <div class="admin-item card" v-for="adm in store.admins" :key="adm.id">
        <div class="admin-main">
          <div class="admin-name-row">
            <span class="admin-name">{{ adm.name }}</span>
            <span class="role-pill" :class="`role-pill-${(adm.role || 'admin').toLowerCase()}`">
              {{ roleLabel(adm.role) }}
            </span>
            <span v-if="adm.id === authStore.admin?.id" class="self-badge">você</span>
          </div>
          <span class="admin-email">{{ adm.email }}</span>
          <span class="admin-meta">cadastrado em {{ formatDate(adm.createdAt) }}</span>
        </div>

        <div class="admin-actions">
          <button v-if="canChangePassword(adm)" class="icon-btn" @click="openChangePassword(adm)" title="Trocar senha">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"><path fill="currentColor" d="M12 17c1.1 0 2-.9 2-2s-.9-2-2-2s-2 .9-2 2s.9 2 2 2zm6-9h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM8.9 6c0-1.71 1.39-3.1 3.1-3.1s3.1 1.39 3.1 3.1v2H8.9V6zM18 20H6V10h12v10z"/></svg>
          </button>
          <button
            class="icon-btn icon-danger"
            v-if="canRemove(adm)"
            @click="confirmDelete(adm)"
            title="Remover usuário"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"><path fill="currentColor" d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>
          </button>
        </div>
      </div>
    </div>

    <EmptyState v-else title="Nenhum usuário ainda" description="Crie o primeiro auxiliar de check-in." />

    <!-- Modal: novo usuário -->
    <div class="modal-overlay" v-if="formVisible" @click.self="closeForm">
      <div class="modal-card">
        <h2 class="modal-title">Novo usuário</h2>
        <form @submit.prevent="handleCreate" class="form">
          <div class="form-group">
            <label class="label" for="adm-name">Nome *</label>
            <input id="adm-name" class="input" type="text" v-model="form.name" autocomplete="off" />
          </div>
          <div class="form-group">
            <label class="label" for="adm-email">Email *</label>
            <input id="adm-email" class="input" type="email" v-model="form.email" autocomplete="off" />
          </div>
          <div class="form-group">
            <label class="label" for="adm-pwd">Senha *</label>
            <input id="adm-pwd" class="input" type="password" v-model="form.password" autocomplete="new-password" placeholder="mínimo 6 caracteres" />
          </div>
          <div class="form-group">
            <label class="label" for="adm-role">Tipo *</label>
            <select id="adm-role" class="input" v-model="form.role" :disabled="!authStore.isRoot">
              <option value="CHECKIN">Voluntário (CHECKIN)</option>
              <option v-if="authStore.isRoot" value="ADMIN">Administrador (ADMIN)</option>
              <option v-if="authStore.isRoot" value="ROOT">Root (ROOT)</option>
            </select>
            <span v-if="!authStore.isRoot" class="hint-msg">
              Como administrador, você só pode criar voluntários.
            </span>
          </div>
          <div class="modal-actions">
            <button type="button" class="btn-secondary" @click="closeForm">Cancelar</button>
            <button type="submit" class="btn-primary" :disabled="creating">
              <span v-if="!creating">Criar</span>
              <span v-else>Criando...</span>
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Modal: trocar senha -->
    <div class="modal-overlay" v-if="pwdTarget" @click.self="pwdTarget = null">
      <div class="modal-card">
        <h2 class="modal-title">Trocar senha</h2>
        <p class="confirm-text">
          Definir nova senha para <strong>{{ pwdTarget.name }}</strong>
          <span v-if="pwdTarget.id === authStore.admin?.id" class="text-muted">(você mesmo)</span>
        </p>
        <form @submit.prevent="handleChangePassword" class="form">
          <div class="form-group">
            <label class="label" for="new-pwd">Nova senha *</label>
            <input id="new-pwd" class="input" type="password" v-model="newPassword" autocomplete="new-password" placeholder="mínimo 6 caracteres" />
          </div>
          <div class="modal-actions">
            <button type="button" class="btn-secondary" @click="pwdTarget = null">Cancelar</button>
            <button type="submit" class="btn-primary" :disabled="changingPwd">
              <span v-if="!changingPwd">Salvar</span>
              <span v-else>Salvando...</span>
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Modal: remover -->
    <div class="modal-overlay" v-if="deleteTarget" @click.self="deleteTarget = null">
      <div class="modal-card">
        <h2 class="modal-title">Remover usuário?</h2>
        <p class="confirm-text">
          Remover <strong>{{ deleteTarget.name }}</strong> (<code>{{ deleteTarget.email }}</code>) permanentemente?
          Esta ação não pode ser desfeita.
        </p>
        <div class="modal-actions">
          <button class="btn-secondary" @click="deleteTarget = null">Cancelar</button>
          <button class="btn-danger" :disabled="deleting" @click="handleDelete">
            <span v-if="!deleting">Remover</span>
            <span v-else>Removendo...</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useAdminsStore } from '../stores/admins'
import { useAuthStore, roleLabel } from '../stores/auth'
import SkeletonLoader from '../components/ui/SkeletonLoader.vue'
import EmptyState from '../components/ui/EmptyState.vue'

export default {
  name: 'UsuariosPage',
  components: { SkeletonLoader, EmptyState },
  data() {
    return {
      formVisible: false,
      form: { name: '', email: '', password: '', role: 'CHECKIN' },
      creating: false,
      pwdTarget: null,
      newPassword: '',
      changingPwd: false,
      deleteTarget: null,
      deleting: false
    }
  },
  computed: {
    store() { return useAdminsStore() },
    authStore() { return useAuthStore() }
  },
  mounted() {
    this.store.fetchAll().catch(err => this.toastError(err, 'Erro ao carregar usuários'))
  },
  methods: {
    roleLabel,
    formatDate(iso) {
      try { return new Date(iso).toLocaleDateString('pt-BR') } catch { return '—' }
    },
    canChangePassword(adm) {
      // ROOT pode trocar de qualquer um. ADMIN só do próprio ou de CHECKIN.
      if (this.authStore.isRoot) return true
      if (adm.id === this.authStore.admin?.id) return true
      return adm.role === 'CHECKIN'
    },
    canRemove(adm) {
      if (adm.id === this.authStore.admin?.id) return false
      if (this.authStore.isRoot) return adm.role !== 'ROOT'
      // ADMIN só remove CHECKIN
      return adm.role === 'CHECKIN'
    },
    toastError(err, fallback) {
      const msg = err?.response?.data?.message || fallback
      this.$toast.open({ message: msg, type: 'error', position: 'top-right', duration: 4000 })
    },
    openNew() {
      this.form = { name: '', email: '', password: '', role: this.authStore.isRoot ? 'ADMIN' : 'CHECKIN' }
      this.formVisible = true
    },
    closeForm() {
      if (this.creating) return
      this.formVisible = false
    },
    async handleCreate() {
      this.creating = true
      try {
        await this.store.create(this.form)
        this.$toast.open({ message: 'Usuário criado', type: 'success', position: 'top-right' })
        this.formVisible = false
      } catch (err) {
        this.toastError(err, 'Erro ao criar usuário')
      } finally {
        this.creating = false
      }
    },
    openChangePassword(adm) {
      this.pwdTarget = adm
      this.newPassword = ''
    },
    async handleChangePassword() {
      if (!this.pwdTarget) return
      this.changingPwd = true
      try {
        await this.store.updatePassword(this.pwdTarget.id, this.newPassword)
        this.$toast.open({ message: 'Senha atualizada', type: 'success', position: 'top-right' })
        this.pwdTarget = null
      } catch (err) {
        this.toastError(err, 'Erro ao trocar senha')
      } finally {
        this.changingPwd = false
      }
    },
    confirmDelete(adm) { this.deleteTarget = adm },
    async handleDelete() {
      if (!this.deleteTarget) return
      this.deleting = true
      try {
        await this.store.remove(this.deleteTarget.id)
        this.$toast.open({ message: 'Usuário removido', type: 'warning', position: 'top-right' })
        this.deleteTarget = null
      } catch (err) {
        this.toastError(err, 'Erro ao remover usuário')
      } finally {
        this.deleting = false
      }
    }
  }
}
</script>

<style scoped>
.page-header {
  display: flex; align-items: center; justify-content: space-between;
  gap: var(--spacing-md); margin-bottom: var(--spacing-sm);
}
.page-header .page-title { margin-bottom: 0; }
.page-subtitle {
  font-size: var(--text-sm); color: var(--color-text-muted);
  margin-bottom: var(--spacing-lg);
}

.admins-list { display: flex; flex-direction: column; gap: var(--spacing-md); }
.admin-item {
  display: flex; align-items: center; justify-content: space-between;
  gap: var(--spacing-base); padding: var(--spacing-base);
  transition: var(--transition-fast);
}
.admin-main { display: flex; flex-direction: column; gap: 4px; flex: 1; min-width: 0; }
.admin-name-row { display: flex; align-items: center; gap: var(--spacing-sm); flex-wrap: wrap; }
.admin-name { font-size: var(--text-md); font-weight: 600; color: var(--color-text-primary); }
.admin-email { font-size: var(--text-sm); color: var(--color-text-secondary); word-break: break-all; }
.admin-meta { font-size: var(--text-xs); color: var(--color-text-muted); }

.role-pill {
  font-size: 10px; font-weight: 800;
  padding: 2px 8px; border-radius: 8px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}
.role-pill-root { background: #fbbf24; color: #422006; }
.role-pill-admin { background: rgba(51, 102, 204, 0.12); color: var(--color-accent); }
.role-pill-checkin { background: var(--color-bg-hover); color: #5f6368; }
.self-badge {
  background: var(--color-bg-hover); color: var(--color-text-muted);
  font-size: 10px; font-weight: 700;
  padding: 2px 8px; border-radius: 8px;
}

.admin-actions { display: flex; gap: var(--spacing-xs); flex-shrink: 0; }
.icon-btn {
  width: 36px; height: 36px;
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--color-bg-hover);
  border: 1px solid var(--color-bg-separator);
  border-radius: var(--radius-md);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: var(--transition-fast);
}
.icon-btn:hover { background: var(--color-accent); color: #fff; border-color: var(--color-accent); }
.icon-btn.icon-danger:hover { background: var(--color-danger); border-color: var(--color-danger); }

.form { display: flex; flex-direction: column; gap: var(--spacing-base); }
.form-group { display: flex; flex-direction: column; }
.confirm-text { font-size: var(--text-sm); color: var(--color-text-secondary); margin-bottom: var(--spacing-base); }
.text-muted { color: var(--color-text-muted); font-size: var(--text-xs); }
code {
  font-family: monospace; background: var(--color-bg-hover);
  padding: 2px 6px; border-radius: var(--radius-sm); font-size: var(--text-xs);
}

@media (max-width: 600px) {
  .admin-item { flex-direction: column; align-items: stretch; }
  .admin-actions { justify-content: flex-end; }
}
</style>
